/**
 * planner_find_keywords — the id source for planner_approve_keywords.
 *
 * Exists because candidates attached to a refusal only help an agent that
 * already guessed close. An agent starting with nothing had no way to reach a
 * feed id at all: `planner_find_articles` returns articles, which cannot match
 * `primary_keyword` and never carries `duplicate_status`.
 *
 * It returns the two gate fields as well as the ids, so the agent can predict
 * a refusal from the read instead of discovering it by attempting a write.
 */
import { z } from 'zod';

import {
  defineCommand,
  refuse,
  type CommitAuthorization,
  type Diff,
  type KeywordCandidateSummary,
  type Receipt,
  type Refusal,
  type ResolveInput,
  type TenantId,
  type WriteDeps,
} from '../contract';

/** Hoisted to module scope so the schema cannot drift between registrations. */
const findKeywordsArgs = z.object({
  /** Feed lifecycle states worth showing. Omitted means all listable ones. */
  status: z.array(z.enum(['candidate', 'approved', 'stale'])).min(1).optional(),
  /** Substring match on the keyword itself. */
  text: z.string().min(2).optional(),
  /** Planning bucket, `YYYY-MM`. Rejected rather than coerced when malformed. */
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'month must be YYYY-MM, for example 2026-09')
    .optional(),
  limit: z.number().int().min(1).max(25).default(25),
});

type FindKeywordsArgs = z.infer<typeof findKeywordsArgs>;

/** The ceiling that keeps one read inside the 1.5K output budget. */
const MAX_ROWS = 25;

interface FoundKeywords {
  readonly rows: readonly KeywordCandidateSummary[];
  /** True when the filter matched the cap exactly, so the agent narrows. */
  readonly capped: boolean;
}

/** One line per row, compact enough that 25 of them stay under the ceiling. */
function renderRow(row: KeywordCandidateSummary): string {
  const flags = [
    row.duplicateStatus === 'clear' ? null : row.duplicateStatus,
    row.verdict === 'swap' ? 'swap' : null,
  ].filter(Boolean);
  const suffix = flags.length ? ` [${flags.join(', ')}]` : '';
  return `${row.keyword} (${row.status}${row.targetMonth ? `, ${row.targetMonth}` : ''})${suffix}`;
}

export const plannerFindKeywords = defineCommand<FindKeywordsArgs, FoundKeywords>({
  name: 'planner_find_keywords',
  section: 'feed',
  consequence: 'reads',
  /**
   * Safe for Emjae because it only reads, and load-bearing because it is the
   * id source: an agent holding no ids cannot reach planner_approve_keywords
   * without it. The write tool stays out of Emjae's list by construction —
   * WriteCommand types the flag `false`.
   */
  exposeToEmjae: true,
  description:
    'List keyword-feed candidates with their ids, status, duplicate verdict and target month. ' +
    'Filter by status, keyword text or target month. Use the ids with planner_approve_keywords; ' +
    'rows marked swap or not clear will be refused there.',
  args: findKeywordsArgs,

  async resolve(input: ResolveInput<FindKeywordsArgs>): Promise<FoundKeywords | Refusal> {
    const { args, tenant, read, signal } = input;
    if (signal.aborted) return refuse('Aborted before reading the feed.', { retryable: true });

    const find = read.findKeywordCandidates;
    if (!find) {
      return refuse('This build has no keyword feed read seam wired.', { retryable: false });
    }

    const rows = await find.call(read, tenant, {
      ...(args.status ? { status: args.status } : {}),
      ...(args.text ? { textContains: args.text } : {}),
      ...(args.month ? { targetMonth: args.month } : {}),
      limit: Math.min(args.limit, MAX_ROWS),
    });

    if (rows.length === 0) {
      return refuse('No keyword feed candidates match those filters.', { retryable: false });
    }
    return { rows, capped: rows.length >= Math.min(args.limit, MAX_ROWS) };
  },

  /** A read changes nothing; the empty change list is the honest diff. */
  diff(_target: FoundKeywords, tenant: TenantId): Diff {
    return {
      table: 'keyword_feed_items',
      rowId: null,
      tenant,
      changes: [],
      reachesGeneratedContent: false,
    };
  },

  async run(
    target: FoundKeywords,
    _auth: CommitAuthorization,
    _write: WriteDeps,
  ): Promise<Receipt | Refusal> {
    const approvable = target.rows.filter(
      (r) => r.duplicateStatus === 'clear' && r.verdict !== 'swap',
    );
    return {
      summary:
        `${target.rows.length} keyword${target.rows.length === 1 ? '' : 's'}` +
        (target.capped ? ' (capped, narrow the filter for more)' : '') +
        `: ${target.rows.map(renderRow).join('; ')}`,
      facts: {
        // Comma-joined so the next tool can take them without parsing prose.
        ids: target.rows.map((r) => r.id).join(','),
        count: target.rows.length,
        approvable: approvable.length,
        capped: target.capped,
      },
    };
  },
});
